import api from 'config/axiosConfig';
import { LocationData } from '../ui/LocationSearchField';
import { apiRoutes } from "util/apiRoutes";

// Types for API responses
interface ApiResponse<T = any> {
    data: T;
    message?: string;
}

// Privacy levels for user data
export type PrivacyLevel = 'public' | 'follow' | 'members' | 'teams' | 'besteas' | 'me' | 'admin' | 'anonymous';
export type MessageReceiveOption = "public" | "followers" | "partea_members" | "besteas" | "select_users" | "no_one";

// Types for different setting categories
export interface PrivacySettings {
    is_private?: boolean;
    content_default_audience?: PrivacyLevel;
    message_receive?: MessageReceiveOption[];
    message_select_users?: number[];
}

export interface PreferenceSettings {
    auth_otp_login?: boolean;
    // Add other preference settings as needed
}

export interface AboutValue<T = any> {
    value: T;
    privacy: PrivacyLevel;
}

export interface AboutSettings {
    bio_brief: AboutValue<string>;
    bio_links: AboutValue<Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>>;
    top_issue: AboutValue<number | null>;
    alignment: AboutValue<Record<string, string>>;
    birthdate: AboutValue<string>;
    age: AboutValue<number>;
    education: AboutValue<string>;
    location: AboutValue<LocationData>;
    religion: AboutValue<string[]>;
    political_identities: AboutValue<string[]>;
    political_parties: AboutValue<string[]>;
    gender: AboutValue<string>;
}

const buildQueryPath = (basePath: string, query: string): string => (
    query.length > 0 ? `${basePath}?${query}` : basePath
);

// Helper function for getting user by id
export async function getUserById(userId: number): Promise<ApiResponse> {
    const response = await api.get(apiRoutes.user.byId(userId));
    return response.data;
}

export async function updateIsPrivate(isPrivate: boolean): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.user.private, {
        is_private: isPrivate
    });
    return response;
}

export async function updateAllowContentPromotion(enabled: boolean): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.user.allowContentPromotion, {
        enabled,
    });
    return response;
}

export type AdBreakContentPreference = "promoted" | "ads" | "mixed";
export type AdBreakConsentPreference = "personalized" | "non_personalized";

export async function updateAdBreakContentPreference(preference: AdBreakContentPreference): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.user.adBreakPreference, {
        preference,
    });
    return response;
}

export async function updateAdBreakConsentPreference(preference: AdBreakConsentPreference): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.user.adBreakConsentPreference, {
        preference,
    });
    return response;
}

// Helper function for privacy settings
export async function updatePrivacySetting<K extends keyof PrivacySettings>(
    category: string,
    key: string,
    value: PrivacySettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.user.privacy, {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating privacy setting:', error);
        throw error;
    }
}

// Helper function for preference settings
export async function updatePreferenceSetting<K extends keyof PreferenceSettings>(
    category: string,
    key: string,
    value: PreferenceSettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.user.preferences, {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating preference setting:', error);
        throw error;
    }
}

// Helper function for about settings
export async function updateAboutSetting<K extends keyof AboutSettings>(
    category: string,
    key: string,
    value: AboutSettings[K] extends AboutValue<infer T> ? T : never,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.user.about, {
            category,
            key,
            value: {
                value,
                privacy
            }
        });
        return response;
    } catch (error) {
        console.error('Error updating about setting:', error);
        throw error;
    }
}

// Specific helper functions for common operations
export async function updatePrivateAccountStatus(isPrivate: boolean): Promise<ApiResponse> {
    return updateIsPrivate(isPrivate);
}

export async function updateDefaultAudience(audience: PrivacyLevel): Promise<ApiResponse> {
    return updatePrivacySetting('content', 'default_audience', audience);
}

export async function updateMessagePrivacy(recipients: PrivacySettings['message_receive']): Promise<ApiResponse> {
    return updatePrivacySetting('message', 'receive', recipients);
}

export async function updateMessageSelectUsers(userIds: number[]): Promise<ApiResponse> {
    return updatePrivacySetting("message", "select_users", userIds);
}

export async function updateOtpLogin(enabled: boolean): Promise<ApiResponse> {
    return updatePreferenceSetting('auth', 'otp_login', enabled);
}

export async function updateUserBio(bio: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'brief', bio, privacy);
}

export async function updateUserLinks(links: Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'links', links, privacy);
}

export async function updateTopIssue(issue: number | null, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'issue', issue, privacy);
}

export async function updateAlignment(alignment: Record<string, string>, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('alignment', 'values', alignment, privacy);
}

// New helper functions for user background information

/**
 * Updates the user's birthdate information
 * @param birthdate - ISO format date string (YYYY-MM-DD)
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioBirthdate(birthdate: string, privacy: PrivacyLevel = 'me'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'birthdate', birthdate, privacy);
}

/**
 * Updates the user's education information
 * @param education - Education information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioEducation(education: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'education', education, privacy);
}

/**
 * Updates the user's gender information
 * @param gender - Gender information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioGender(gender: string, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'gender', gender, privacy);
}

/**
 * Updates the user's age information
 * @param age - Age information as string
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioAge(age: number, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'age', age, privacy);
}

/**
 * Updates the user's location information
 * @param location - Location data object
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioLocation(location: LocationData, privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'location', location, privacy);
}

/**
 * Removes the user's saved location from about/background entirely.
 * This is used when a user explicitly clears their location field.
 *
 * Backend behavior: when `value` is `null`, the `about_bio_location` key is deleted from `properties`.
 *
 * @param privacy - Included for signature symmetry; not used for deletions.
 * @returns API response object
 */
export async function clearBioLocation(privacy: PrivacyLevel = "public"): Promise<ApiResponse> {
    void privacy; // Explicitly unused; kept to avoid breaking future call sites.
    const response = await api.patch(apiRoutes.user.about, {
        category: "bio",
        key: "location",
        value: null
    });
    return response;
}

/**
 * Updates the user's religious affiliation information
 * @param religions - Array of religious affiliations as strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioReligion(religion: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'religion', religion, privacy);
}

/**
 * Updates the user's political identities
 * @param identities - Array of political identity strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioPoliticalIdentities(identities: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'political_identities', identities, privacy);
}

/**
 * Updates the user's political party affiliations
 * @param parties - Array of political party strings
 * @param privacy - Privacy level for this information
 * @returns API response object
 */
export async function updateBioPoliticalParties(parties: string[], privacy: PrivacyLevel = 'public'): Promise<ApiResponse> {
    return updateAboutSetting('bio', 'political_parties', parties, privacy);
}

// User Relations API Helper Functions

export interface UserRelation {
    id: number;
    userId: number;
    relNodeType: string;
    relNodeId: number;
    relType: string;
    relHash: string;
    relDescription?: string;
    properties: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

export interface CreateRelationRequest {
    relNodeType: string;
    relNodeId: number;
    relType: string;
    relDescription?: string;
    properties?: Record<string, any>;
}

export interface UpdateRelationRequest {
    relDescription?: string;
    properties?: Record<string, any>;
}

/**
 * Creates a new user relation
 * @param relationData - Data for the new relation
 * @returns API response with created relation
 */
export async function createUserRelation(relationData: CreateRelationRequest): Promise<ApiResponse<UserRelation>> {
    try {
        const response = await api.post(apiRoutes.user.relations, relationData);
        return response.data;
    } catch (error) {
        console.error('Error creating user relation:', error);
        throw error;
    }
}

/**
 * Updates an existing user relation
 * @param relationId - ID of the relation to update
 * @param updateData - Data to update
 * @returns API response with updated relation
 */
export async function updateUserRelation(relationId: number, updateData: UpdateRelationRequest): Promise<ApiResponse<UserRelation>> {
    try {
        const response = await api.put(`${apiRoutes.user.relations}/${relationId}`, updateData);
        return response.data;
    } catch (error) {
        console.error('Error updating user relation:', error);
        throw error;
    }
}

/**
 * Deletes a user relation
 * @param relationId - ID of the relation to delete
 * @returns API response
 */
export async function deleteUserRelation(relationId: number): Promise<ApiResponse> {
    try {
        const response = await api.delete(`${apiRoutes.user.relations}/${relationId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting user relation:', error);
        throw error;
    }
}

/**
 * Gets user relations with optional filtering
 * @param filters - Optional filters for the relations
 * @returns API response with relations
 */
export async function getUserRelations(filters?: {
    relTypes?: string[];
    relNodeTypes?: string[];
    relNodeIds?: number[];
    privacyLevels?: PrivacyLevel[];
    relatedTypes?: string[];
    relatedIds?: number[];
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
}): Promise<ApiResponse<{ relations: UserRelation[]; total: number }>> {
    try {
        const params = new URLSearchParams();
        
        if (filters) {
            if (filters.relTypes) params.append('relTypes', filters.relTypes.join(','));
            if (filters.relNodeTypes) params.append('relNodeTypes', filters.relNodeTypes.join(','));
            if (filters.relNodeIds) params.append('relNodeIds', filters.relNodeIds.join(','));
            if (filters.privacyLevels) params.append('privacyLevels', filters.privacyLevels.join(','));
            if (filters.relatedTypes) params.append('relatedTypes', filters.relatedTypes.join(','));
            if (filters.relatedIds) params.append('relatedIds', filters.relatedIds.join(','));
            if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
            if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
            if (filters.sortBy) params.append('sortBy', filters.sortBy);
            if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        }
        
        const response = await api.get(buildQueryPath(`${apiRoutes.user.relations}/my`, params.toString()));
        return response.data;
    } catch (error) {
        console.error('Error getting user relations:', error);
        throw error;
    }
}

/**
 * Gets user relations for a specific user
 * @param userId - ID of the user
 * @param filters - Optional filters for the relations
 * @returns API response with relations
 */
export async function getUserRelationsByUserId(userId: number, filters?: {
    relTypes?: string[];
    relNodeTypes?: string[];
    relNodeIds?: number[];
    privacyLevels?: PrivacyLevel[];
    relatedTypes?: string[];
    relatedIds?: number[];
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
}): Promise<any> {
    try {
        const params = new URLSearchParams();
        
        if (filters) {
            if (filters.relTypes) params.append('relTypes', filters.relTypes.join(','));
            if (filters.relNodeTypes) params.append('relNodeTypes', filters.relNodeTypes.join(','));
            if (filters.relNodeIds) params.append('relNodeIds', filters.relNodeIds.join(','));
            if (filters.privacyLevels) params.append('privacyLevels', filters.privacyLevels.join(','));
            if (filters.relatedTypes) params.append('relatedTypes', filters.relatedTypes.join(','));
            if (filters.relatedIds) params.append('relatedIds', filters.relatedIds.join(','));
            if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
            if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
            if (filters.sortBy) params.append('sortBy', filters.sortBy);
            if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        }
        
        const response = await api.get(buildQueryPath(`${apiRoutes.user.relations}/user/${userId}`, params.toString()));
        return response.data;
    } catch (error) {
        console.error('Error getting user relations by user ID:', error);
        throw error;
    }
}

// Issue Ranking Specific Helper Functions

export interface IssueRanking {
    id: number;
    userId: number;
    issueId: number;
    ranking: number;
    privacy: PrivacyLevel;
    relatedType?: string;
    relatedId?: number;
    createdAt: string;
    updatedAt: string;
    issue?: {
        id: number;
        name: string;
        cname: string;
        properties?: any;
    };
}

/**
 * Gets the user's issue rankings
 * @param userId - Optional user ID (defaults to current user)
 * @returns API response with issue rankings
 */
export async function getIssueRankings(userId?: number): Promise<ApiResponse<IssueRanking[]>> {
    try {
        const url = userId 
            ? `${apiRoutes.user.relations}/user/${userId}?relTypes=issue_ranking&relNodeTypes=issue&limit=100`
            : `${apiRoutes.user.relations}/my?relTypes=issue_ranking&relNodeTypes=issue&limit=100`;
        const response = await api.get(url);
        
        // Transform generic relations response to IssueRanking format
        if (response.data && response.data.relations) {
            // Get issue details for each relation
            const rankings: IssueRanking[] = await Promise.all(
                response.data.relations.map(async (relation: any) => {
                    try {
                        // Fetch issue details
                        const issueResponse = await api.get(apiRoutes.issues.byId(relation.rel_node_id));
                        const issue = issueResponse.data;
                        
                        return {
                            id: relation.id,
                            userId: relation.user_id,
                            issueId: relation.rel_node_id,
                            ranking: relation.properties?.value || 1,
                            privacy: relation.properties?.privacy || 'public',
                            relatedType: relation.properties?.related_type,
                            relatedId: relation.properties?.related_id,
                            createdAt: relation.created_at,
                            updatedAt: relation.updated_at,
                            issue: {
                                id: issue.id,
                                name: issue.name,
                                cname: issue.cname,
                                properties: issue.properties
                            }
                        } as IssueRanking;
                    } catch (error) {
                        console.error(`Error fetching issue ${relation.rel_node_id}:`, error);
                        // Return a basic ranking if issue fetch fails
                        return {
                            id: relation.id,
                            userId: relation.user_id,
                            issueId: relation.rel_node_id,
                            ranking: relation.properties?.value || 1,
                            privacy: relation.properties?.privacy || 'public',
                            relatedType: relation.properties?.related_type,
                            relatedId: relation.properties?.related_id,
                            createdAt: relation.created_at,
                            updatedAt: relation.updated_at,
                            issue: {
                                id: relation.rel_node_id,
                                name: `Issue ${relation.rel_node_id}`,
                                cname: `issue-${relation.rel_node_id}`,
                                properties: {}
                            }
                        } as IssueRanking;
                    }
                })
            );
            
            // Sort by ranking value
            rankings.sort((a, b) => a.ranking - b.ranking);
            
            return {
                data: rankings,
                message: response.data.message
            };
        }
        
        return {
            data: [],
            message: response.data?.message
        };
    } catch (error) {
        console.error('Error getting issue rankings:', error);
        throw error;
    }
}

/**
 * Adds or updates an issue ranking
 * @param issueId - ID of the issue to rank
 * @param ranking - Ranking position (1-100)
 * @param privacy - Privacy level for this ranking
 * @param relatedType - Optional related type (e.g., 'comment')
 * @param relatedId - Optional related ID
 * @returns API response with the relation
 */
export async function addIssueRanking(
    issueId: number, 
    ranking: number, 
    privacy: PrivacyLevel = 'public',
    relatedType?: string,
    relatedId?: number
): Promise<any> {
    try {
        const properties: Record<string, any> = {
            value: ranking,
            privacy
        };
        
        if (relatedType) properties.related_type = relatedType;
        if (relatedId) properties.related_id = relatedId;
        
        const response = await createUserRelation({
            relNodeType: 'issue',
            relNodeId: issueId,
            relType: 'issue_ranking',
            properties
        });

        return response;
    } catch (error) {
        console.error('Error adding issue ranking:', error);
        throw error;
    }
}

/**
 * Updates an existing issue ranking
 * @param relationId - ID of the relation to update
 * @param ranking - New ranking position (1-100)
 * @param privacy - Privacy level for this ranking
 * @param relatedType - Optional related type (e.g., 'comment')
 * @param relatedId - Optional related ID
 * @returns API response with updated relation
 */
export async function updateIssueRanking(
    relationId: number,
    ranking: number,
    privacy: PrivacyLevel = 'public',
    relatedType?: string,
    relatedId?: number
): Promise<ApiResponse<UserRelation>> {
    try {
        const properties: Record<string, any> = {
            value: ranking,
            privacy
        };
        
        if (relatedType) properties.related_type = relatedType;
        if (relatedId) properties.related_id = relatedId;
        
        return await updateUserRelation(relationId, { properties });
    } catch (error) {
        console.error('Error updating issue ranking:', error);
        throw error;
    }
}

/**
 * Removes an issue ranking
 * @param relationId - ID of the relation to delete
 * @returns API response
 */
export async function removeIssueRanking(relationId: number): Promise<ApiResponse> {
    try {
        return await deleteUserRelation(relationId);
    } catch (error) {
        console.error('Error removing issue ranking:', error);
        throw error;
    }
}

/**
 * Reorders multiple issue rankings at once
 * @param rankings - Array of objects with relationId and new ranking position
 * @returns API response
 */
export async function reorderIssueRankings(rankings: Array<{ relationId: number; ranking: number }>): Promise<ApiResponse> {
    try {
        const response = await api.put(apiRoutes.user.issueRankingsReorder, { rankings });
        return response.data;
    } catch (error) {
        console.error('Error reordering issue rankings:', error);
        throw error;
    }
}

/**
 * Gets the next available ranking number for issue rankings
 * @returns Promise with the next ranking number
 */
export async function getNextIssueRankingPosition(): Promise<number> {
    try {
        const response = await getIssueRankings();
        const rankings = response.data || [];
        
        if (rankings.length === 0) return 1;
        
        // Find the highest ranking value and add 1
        const maxRanking = Math.max(...rankings.map(r => r.ranking));
        return maxRanking + 1;
    } catch (error) {
        console.error('Error getting next ranking position:', error);
        // If there's an error, try to get a safe next position
        try {
            // Fallback: get generic relations and count them
            const fallbackResponse = await getUserRelations({
                relTypes: ['issue_ranking'],
                relNodeTypes: ['issue'],
                limit: 100
            });
            const relationCount = fallbackResponse.data?.relations?.length || 0;
            return relationCount + 1;
        } catch (fallbackError) {
            console.error('Error in fallback ranking calculation:', fallbackError);
            return 1;
        }
    }
} 